
import React from 'react';
import { useLanguage } from './LanguageContext';
import { TRANSLATIONS, PRACTICE_AREAS, ICON_MAP, PAGE_CONTENT } from '../constants';
import { ArrowRight } from 'lucide-react';
import { ViewType } from '../types';

interface PracticeAreasProps {
  onNavigate: (view: ViewType) => void;
}

const PracticeAreas: React.FC<PracticeAreasProps> = ({ onNavigate }) => {
  const { language } = useLanguage();
  const t = TRANSLATIONS[language];
  const practices = PRACTICE_AREAS[language];

  const handlePracticeClick = (id: string) => {
    // Navigate to specific static page if ID exists in PAGE_CONTENT, otherwise scroll to contact
    const hasMoreInfo = !!PAGE_CONTENT[language][id];

    if (hasMoreInfo) {
      onNavigate(id as ViewType);
    } else {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="practices" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-20">
          <span className="text-[#c5a059] font-bold text-sm tracking-[0.3em] uppercase mb-4 block animate-in fade-in duration-1000">Our Expertise</span>
          <h2 className="text-4xl md:text-6xl font-serif font-bold mb-6">{t.practiceTitle}</h2>
          <div className="w-24 h-1.5 bg-[#c5a059] mx-auto mb-8"></div>
          <p className="text-gray-600 max-w-2xl mx-auto text-xl font-light">{t.practiceSubtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {practices.map((practice) => {
            const hasMoreInfo = !!PAGE_CONTENT[language][practice.id];

            return (
              <div
                key={practice.id}
                className="group relative bg-[#fafafa] p-12 border border-gray-100 transition-all duration-500 hover:bg-white hover:shadow-2xl hover:-translate-y-3 cursor-pointer text-center flex flex-col items-center"
                onClick={() => handlePracticeClick(practice.id)}
              >
                <div className="text-[#c5a059] mb-10 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500 bg-[#c5a059]/5 p-5 rounded-full">
                  {ICON_MAP[practice.icon]}
                </div>
                <h3 className="text-2xl font-bold mb-5 font-serif">{practice.title}</h3>
                <p className="text-gray-500 mb-10 leading-relaxed font-light">
                  {practice.description}
                </p>

                {hasMoreInfo && (
                  <div className="mt-auto flex items-center text-[#c5a059] font-bold text-xs tracking-[0.2em] uppercase group-hover:translate-x-2 transition-transform duration-300 border-b border-transparent hover:border-[#c5a059] pb-1">
                    {language === 'EN' ? 'Learn More' : 'Mëso më shumë'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                )}

                {/* Subtle card accent line */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-[#c5a059] group-hover:w-full transition-all duration-500"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PracticeAreas;
